/* 
Switches between tabs. See examples in TabsBlock, ProfileListPage, and ProductSolutionsOverviewPage.
*/

type ToggleTabsCallback = (tabId: string) => void;

export const toggleTabs = (tabToggledCallback?: ToggleTabsCallback) => {

  /**
   * Checks if an element has all the specified CSS classes.
   * @param element
   * @param cssClasses All CSS classes that must be present on the element.
   */
  const hasAllCssClasses = (element: HTMLElement, cssClasses: string[]) => {
    let hasAll = true; // Considered true until proven otherwise

    for (const cssClass of cssClasses) {
      if (!element.classList.contains(cssClass)) {
        hasAll = false;
        break;
      }
    }

    return hasAll;
  };

  const tabsContainers = document.querySelectorAll<HTMLElement>(
    "[data-active-tab-classes]"
  ); // There may be multiple on the same page

  if (!tabsContainers) {
    return;
  }

  tabsContainers.forEach(tabsContainer => {
    const tabs = tabsContainer.querySelectorAll<HTMLElement>("[data-tab]");

    if (!tabs) {
      console.warn("There were no tabs in the tabs container.");

      return;
    }

    // Split the list of CSS classes to be able to confidently check if they're all part of an element's class attribute
    const activeCssClasses = (tabsContainer.dataset.activeTabClasses || "")
      .split(" ")
      .filter(x => x.length);

    if (activeCssClasses.length === 0) {
      console.warn(
        "No CSS classes specified for the active tab, tabs will not change when clicked."
      );

      return;
    }

    for (let i = 0; i < tabs.length; i++) {
      tabs[i].addEventListener("click", event => {
        const clickedTab = event.target as HTMLElement;

        const isActive = hasAllCssClasses(clickedTab, activeCssClasses);

        if (isActive) {
          // Clicked already active tab
          return;
        }

        const tabId = clickedTab.dataset.tabId || "";

        if (!tabId) {
          throw "Unable to determine current tab";
        }

        // Deselect all other tabs
        for (const otherTab of tabs) {
          otherTab.classList.remove(...activeCssClasses);
        }

        // Select the clicked tab
        clickedTab.classList.add(...activeCssClasses);

        // Make the corresponding tab content visible
        tabsContainer
          .querySelectorAll<HTMLElement>(`[data-tab-content]`)
          .forEach(tabContent => {
            if (tabContent.dataset.tabId === tabId) {
              tabContent.classList.remove("hidden");
            } else {
              tabContent.classList.add("hidden");
            }
          });
        
        if (tabToggledCallback) {
          tabToggledCallback(tabId);
        }
      });
    }
  });
};
